import type { EventBus } from '../core/events'
import type { DaiseEventKey } from './bank'
import { filterLine, filterReport } from './voiceFilter'

/**
 * Live DAISE client (issue #38) — talks to the Worker endpoints shipped in
 * issue #37. The bank remains the instant, always-available path; live lines
 * only ever *supplement* it, and every failure mode (offline, toggle off, no
 * Worker, no key, timeout, filter rejection) silently degrades to bank-only
 * with zero console noise.
 *
 * DETERMINISM INVARIANT: this module is a read-only observer of the event
 * bus. It never emits events, never touches world/sim state, and consumes no
 * seeded RNG stream — live-line substitution is presentation-only, gated by a
 * plain counter. Same seed ⇒ same simulation, with live mode on or off.
 */

export interface DaiseLineProvider {
  /** Return the line to present for an event. `bankLine` is the offline pick. */
  line(key: DaiseEventKey, bankLine: string): string
}

/** bank-only provider: the offline pick, verbatim */
export class BankLineProvider implements DaiseLineProvider {
  line(_key: DaiseEventKey, bankLine: string): string {
    return bankLine
  }
}

/** knobs live in tuning.daise.live — see src/config/tuning.ts */
export interface LiveTuning {
  liveEvery: number
  lowWater: number
  poolCap: number
  requestCooldown: number
  fetchTimeoutMs: number
  reportTimeoutMs: number
}

interface RingEntry {
  k: string
  v?: string | number
}

/** clamp free text into the server schema's charset/length (worker rejects otherwise) */
function sanitize(s: string): string {
  const t = s.replace(/[^\w .,:%+'-]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 48)
  return t.length > 0 ? t : 'unspecified'
}

/**
 * The real provider: maintains a compact PII-free run snapshot from bus
 * events, prefetches candidate lines asynchronously (presentation never
 * waits), and substitutes at most 1 in `liveEvery` presented lines.
 *
 * All live traffic is gated behind one GET /api/health probe per session:
 * only a Worker that answers ok with the secret present ever receives a
 * snapshot. A static host (no Worker) fails the probe once and the session
 * stays bank-only — no request loops, no retries against nothing.
 */
export class LiveLineProvider implements DaiseLineProvider {
  private mode: 'campaign' | 'endless' = 'campaign'
  private floor = 1
  private act = 1
  private cause: string | undefined
  private credits = 0
  private spent = 0
  private ring: RingEntry[] = []

  private pool: string[] = []
  private counter = 0
  private inflight = false
  private lastRequestAt = 0
  private settingOn = false
  /** null = probe not yet attempted; false = probed and dead for the session */
  private serverReady: boolean | null = null
  private probing = false

  constructor(private t: LiveTuning) {}

  /** read-only bus subscriptions — see the determinism invariant above */
  observe(bus: EventBus): void {
    const push = (k: string, v?: string | number) => {
      const e: RingEntry = v === undefined ? { k } : { k, v: typeof v === 'string' ? sanitize(v) : v }
      this.ring.push(e)
      if (this.ring.length > 6) this.ring.shift()
      this.maybePrefetch()
    }
    bus.on('run_start', ({ mode }) => {
      this.mode = mode
      this.floor = 1
      this.act = 1
      this.cause = undefined
      this.credits = 0
      this.spent = 0
      this.ring = []
      this.pool = []
      push('run_start')
    })
    bus.on('floor_transition', ({ floor, act }) => {
      this.floor = floor
      this.act = act
      // stale candidates reference the floor above — drop them
      this.pool = []
      push('floor_transition', floor)
    })
    bus.on('score_changed', ({ score }) => (this.credits = Math.max(0, score)))
    bus.on('player_hurt', ({ by }) => (this.cause = sanitize(by)))
    bus.on('requisition_purchase', ({ id, cost }) => {
      this.spent += cost
      push('requisition_purchase', id)
    })
    bus.on('boss_intro', ({ bossId }) => push('boss_intro', bossId))
    bus.on('boss_kill', ({ bossId }) => push('boss_kill', bossId))
    bus.on('generator_destroyed', ({ remaining }) => push('generator_destroyed', remaining))
    bus.on('level_clear', () => push('level_clear'))
    bus.on('treasure', () => push('treasure'))
    bus.on('armament_pickup', ({ id }) => push('armament_pickup', id))
    bus.on('override_engaged', () => push('override_engaged'))
    bus.on('amenity_purchase', ({ id }) => push('amenity_purchase', id))
    bus.on('player_starving', () => push('starving'))
    bus.on('life_lost', ({ remaining }) => push('life_lost', remaining))
    bus.on('secret_found', () => push('secret_found'))
    bus.on('relay_latched', ({ remaining }) => push('relay_latched', remaining))
  }

  /** settings toggle (LIVE NETWORK COMMENTARY) — default-on per issue #36 */
  setEnabled(on: boolean): void {
    this.settingOn = on
    if (on) void this.probe()
    // off means off now — buffered candidates don't outlive the toggle
    else this.pool = []
  }

  /** end screens ask this to decide whether to render the report block at all */
  reportAvailable(): boolean {
    return this.settingOn && this.serverReady === true && navigator.onLine
  }

  line(key: DaiseEventKey, bankLine: string): string {
    this.counter++
    this.maybePrefetch()
    // at most 1 in liveEvery presented lines is live; plain counter, no RNG
    if (this.settingOn && this.pool.length > 0 && this.counter % this.t.liveEvery === 0) {
      void key
      return this.pool.shift()!
    }
    return bankLine
  }

  /**
   * End-of-run incident report. Resolves to display text or null — callers
   * collapse the block quietly on null; there is no error state by design.
   */
  async requestReport(summary: { won: boolean; score: number; timeSec: number; kills: number; archive: number; daily: boolean }): Promise<string | null> {
    if (!this.reportAvailable()) return null
    try {
      const body = {
        mode: summary.daily ? 'daily' : this.mode,
        won: summary.won,
        floor: this.floor,
        score: Math.min(99_999_999, Math.max(0, Math.floor(summary.score))),
        timeSec: Math.min(359_999, Math.max(0, Math.floor(summary.timeSec))),
        kills: Math.min(999_999, Math.max(0, Math.floor(summary.kills))),
        archive: Math.min(18, Math.max(0, summary.archive)),
        ...(this.cause !== undefined ? { cause: this.cause } : {}),
        events: this.ring.map((e) => e.k).slice(-10)
      }
      const res = await fetch('/api/daise/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(this.t.reportTimeoutMs)
      })
      if (res.status !== 200) return null
      const data = (await res.json()) as { report?: string }
      // braces: mirror the server's voice filter before presenting
      return typeof data.report === 'string' ? filterReport(data.report) : null
    } catch {
      return null
    }
  }

  /** one health probe per session decides whether live traffic ever flows */
  private async probe(): Promise<void> {
    if (this.serverReady !== null || this.probing || !navigator.onLine) return
    this.probing = true
    try {
      const res = await fetch('/api/health', { signal: AbortSignal.timeout(this.t.fetchTimeoutMs) })
      if (res.status === 200) {
        const data = (await res.json()) as { ok?: boolean; openrouterKey?: boolean }
        this.serverReady = data.ok === true && data.openrouterKey === true
      } else {
        this.serverReady = false
      }
    } catch {
      // offline blip: leave null so a later attempt may re-probe when online
      this.serverReady = navigator.onLine ? false : null
    } finally {
      this.probing = false
    }
  }

  private maybePrefetch(): void {
    if (!this.settingOn || !navigator.onLine) return
    if (this.serverReady === null) {
      void this.probe()
      return
    }
    if (this.serverReady !== true || this.inflight) return
    if (this.pool.length >= this.t.lowWater) return
    const now = Date.now()
    if (now - this.lastRequestAt < this.t.requestCooldown * 1000) return
    this.lastRequestAt = now
    this.inflight = true
    void this.fetchLines().finally(() => (this.inflight = false))
  }

  private async fetchLines(): Promise<void> {
    try {
      const body = {
        mode: this.mode,
        floor: Math.min(99, this.floor),
        act: Math.min(3, Math.max(1, this.act)),
        events: this.ring.slice(-6),
        ...(this.cause !== undefined ? { cause: this.cause } : {}),
        economy: { credits: Math.min(999_999, this.credits), spent: Math.min(999_999, this.spent) }
      }
      const res = await fetch('/api/daise/line', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(this.t.fetchTimeoutMs)
      })
      if (res.status !== 200) return
      const data = (await res.json()) as { lines?: unknown }
      if (!Array.isArray(data.lines)) return
      for (const raw of data.lines) {
        if (this.pool.length >= this.t.poolCap) break
        if (typeof raw !== 'string') continue
        // braces: mirror the server's voice filter before presenting
        const clean = filterLine(raw)
        if (clean) this.pool.push(clean)
      }
    } catch {
      // silent by contract — the bank is the fallback, not an error state
    }
  }
}
